// @ts-check
// Ayuda pedagógica GRADUADA (roadmap §10): revela los niveles de uno en uno, a
// petición del alumno. Vista de la capa `components`: recibe los niveles ya
// redactados (`features/attempt-feedback.js`) y solo los pinta; no los calcula,
// no conoce ejercicios ni intentos y no decide la penalización.
//
// ## Por qué se revelan de uno en uno y no todos a la vez
// El §10 es explícito: la ayuda ESCALA. Volcar los peldaños juntos convierte la
// ayuda en una solución servida y anula el valor pedagógico. Además, el docente
// debe poder limitar cuántos niveles hay disponibles: `nivelMaximo` deja el
// hueco para esa configuración (MOD-ADMIN); por defecto no limita.
//
// ## Peldaños que CUESTAN puntos (decisión del usuario, 2026-07-31)
// Un peldaño con `cuesta: true` casi resuelve el ejercicio, así que penaliza la
// puntuación. Tres reglas que este componente hace cumplir:
//   1. **Se avisa ANTES**, no después, y con el coste explícito en el texto.
//   2. **Hay confirmación en dos pasos** (mismo patrón sin modal que ya usa
//      "Pedir pista" en los paneles de intento): un clic arma, otro confirma,
//      `Escape` cancela y devuelve el foco.
//   3. **El cobro es REAL y lo hace el servidor, no este componente.**
//
// ## Por qué el peldaño de pago se ENSEÑA BLOQUEADO en vez de cobrarse aquí
// Corregir CIERRA el intento en el servidor y fija su puntuación (DEC-064
// punto 5): cuando estos peldaños se muestran, ya no hay intento abierto que
// penalizar. Cobrar en ese momento sería imposible, y anunciar un descuento
// que no ocurre sería mentir al alumno. Por eso el peldaño de pago:
//   - se muestra DESBLOQUEADO si el alumno ya pidió la pista en ese intento
//     (`yaPagado`): ya la pagó, sería absurdo cobrársela otra vez;
//   - se muestra BLOQUEADO en caso contrario, diciendo dónde conseguirla (el
//     botón "Pedir pista" del panel, durante un intento abierto) y cuánto
//     cuesta. Ese botón sí descuenta de verdad, en SERVIDOR (DEC-064/DEC-065).
// Así no hay dos mecanismos de penalización compitiendo: hay UNO, el de
// siempre, y esta ayuda se limita a señalarlo con honestidad.
//
// Texto SIEMPRE con `textContent` (nunca `innerHTML`): parte del contenido
// procede de la salida del propio alumno, que es dato NO confiable (SEC-R1).
// El coste de la pista ("10 % de la puntuación") se compone a partir de una
// única constante compartida con los paneles de intento, espejo de cliente
// del `round(puntuacion_xp * 0.10)` del backend. Este componente sigue sin
// decidir ni aplicar la penalización: solo la NOMBRA.
import { COSTE_PISTA_AYUDA_GRADUADA } from '../utils/penalizacion-pista.js';
import './graded-feedback.css';

/**
 * @typedef {import('../features/attempt-feedback.js').NivelAyuda} NivelAyuda
 */

/**
 * @typedef {object} AyudaGraduada
 * @property {HTMLElement} elemento Raíz, para insertar donde corresponda.
 * @property {(niveles: NivelAyuda[], opciones?: { yaPagado?: boolean }) => void} render Repinta y reinicia la progresión.
 * @property {() => number} nivelesVisibles Cuántos peldaños se han revelado.
 */

/**
 * @param {{ nivelMaximo?: number, costeTexto?: string }} [opciones]
 * @returns {AyudaGraduada}
 */
export function crearAyudaGraduada(opciones = {}) {
  const nivelMaximo =
    typeof opciones.nivelMaximo === 'number' ? opciones.nivelMaximo : Number.POSITIVE_INFINITY;
  const costeTexto = opciones.costeTexto ?? COSTE_PISTA_AYUDA_GRADUADA;

  /** @type {NivelAyuda[]} */
  let niveles = [];
  let revelados = 0;
  let yaPagado = false;

  const seccion = document.createElement('section');
  seccion.className = 'graded-feedback';
  seccion.hidden = true;

  const titulo = document.createElement('h4');
  titulo.className = 'graded-feedback-title';
  titulo.textContent = 'Ayuda para seguir tú solo';

  const intro = document.createElement('p');
  intro.className = 'graded-feedback-intro';
  intro.textContent =
    'Pistas de menos a más concretas. Pide la siguiente solo si la anterior no te ha bastado: pensarlo tú es lo que te hace aprender.';

  const lista = document.createElement('ol');
  lista.className = 'graded-feedback-list';
  lista.setAttribute('aria-live', 'polite');

  const boton = document.createElement('button');
  boton.type = 'button';
  boton.className = 'graded-feedback-button';

  // Peldaño de pago aún no desbloqueado: se anuncia su existencia y su coste,
  // pero NO su contenido. El candado "🔒" acompaña a un texto que ya lo dice
  // todo, así que no se depende del icono ni del color (SC 1.4.1).
  const candado = document.createElement('div');
  candado.className = 'graded-feedback-locked';
  candado.hidden = true;

  const candadoTitulo = document.createElement('p');
  candadoTitulo.className = 'graded-feedback-locked-title';

  const candadoTexto = document.createElement('p');
  candadoTexto.className = 'graded-feedback-locked-text';

  candado.append(candadoTitulo, candadoTexto);

  const agotado = document.createElement('p');
  agotado.className = 'graded-feedback-exhausted';
  agotado.hidden = true;
  agotado.textContent =
    'No hay más pistas automáticas. Si sigues atascado, pregunta a tu profesor.';

  seccion.append(titulo, intro, lista, boton, candado, agotado);

  /** Peldaños que el docente permite ver (`nivelMaximo`). */
  function permitidos() {
    return niveles.filter((nivel) => nivel.nivel <= nivelMaximo);
  }

  /**
   * Peldaños que el alumno puede DESTAPAR ahora mismo: todos los gratuitos
   * hasta el primero de pago que no haya pagado (ese queda bloqueado, y con él
   * los siguientes: la ayuda escala, no se salta peldaños).
   */
  function revelables() {
    const lista = permitidos();
    const bloqueado = lista.findIndex((nivel) => nivel.cuesta && !yaPagado);
    return bloqueado === -1 ? lista : lista.slice(0, bloqueado);
  }

  /** El peldaño de pago que está bloqueado ahora, si lo hay. */
  function bloqueadoPendiente() {
    if (yaPagado) return undefined;
    return permitidos().find((nivel) => nivel.cuesta);
  }

  function pintarControles() {
    const quedan = revelables().length - revelados;
    const bloqueado = bloqueadoPendiente();

    boton.hidden = quedan <= 0;
    if (quedan > 0) {
      boton.textContent = revelados === 0 ? 'Dame una pista' : 'Necesito otra pista';
      boton.title = quedan === 1 ? 'Queda 1 pista gratuita' : `Quedan ${quedan} pistas gratuitas`;
    }

    // El candado solo aparece cuando ya se han agotado las gratuitas: enseñar
    // "hay una mejor de pago" antes de tiempo empujaría a saltarse el proceso.
    const mostrarCandado = quedan <= 0 && bloqueado !== undefined;
    candado.hidden = !mostrarCandado;
    if (mostrarCandado) {
      candadoTitulo.textContent = `🔒 ${bloqueado.titulo}`;
      candadoTexto.textContent = `Esta pista casi resuelve el ejercicio, así que no es gratis. ${costeTexto}`;
    }

    agotado.hidden = !(quedan <= 0 && bloqueado === undefined && permitidos().length > 0);
  }

  /** @param {NivelAyuda} nivel */
  function pintarPeldano(nivel) {
    const item = document.createElement('li');
    item.className = 'graded-feedback-item';
    if (nivel.cuesta) item.dataset.cuesta = 'true';

    const encabezado = document.createElement('p');
    encabezado.className = 'graded-feedback-item-title';
    // Si es de pago, el alumno debe poder ver DESPUÉS por qué bajó su nota.
    encabezado.textContent = nivel.cuesta ? `${nivel.titulo} (pista penalizada)` : nivel.titulo;

    const cuerpo = document.createElement('p');
    cuerpo.className = 'graded-feedback-item-text';
    cuerpo.textContent = nivel.texto;

    item.append(encabezado, cuerpo);
    lista.append(item);
  }

  function revelarSiguiente() {
    const nivel = revelables()[revelados];
    if (nivel === undefined) return;
    revelados += 1;
    pintarPeldano(nivel);
    pintarControles();
  }

  boton.addEventListener('click', revelarSiguiente);

  /**
   * @param {NivelAyuda[]} nuevos
   * @param {{ yaPagado?: boolean }} [config] `yaPagado` = el alumno ya pidió la
   *   pista de este intento, así que los peldaños de pago se le abren sin
   *   volver a cobrarle (el servidor tampoco cobraría dos veces:
   *   `pista_solicitada` es un booleano).
   */
  function render(nuevos, config = {}) {
    niveles = Array.isArray(nuevos) ? nuevos : [];
    revelados = 0;
    yaPagado = config.yaPagado === true;
    lista.replaceChildren();
    seccion.hidden = permitidos().length === 0;
    pintarControles();
  }

  return { elemento: seccion, render, nivelesVisibles: () => revelados };
}
